// The column data types the backend accepts, each paired with the Lucide glyph
// that stands for it. Shared on purpose: the Add-column picker and the entries
// table's column headers must show the SAME glyph for a type, and a second copy
// would drift the moment a type is added.
//
// Labels and hints come from the i18n bundle as
// `datasource_column_type_<value>` / `..._hint`. Read a label through
// columnTypeLabel rather than interpolating `dataType` into that key yourself —
// only the accessor applies the unknown-type fallback.

export const COLUMN_TYPES = [
  { value: 'text', icon: 'type' },
  { value: 'number', icon: 'hash' },
  { value: 'dateTime', icon: 'calendar-clock' },
  { value: 'geoCoordinates', icon: 'map-pin' },
  { value: 'phoneNumber', icon: 'phone' },
] as const;

/** The known type a `dataType` resolves to, or `text` when the backend has added
 *  one the frontend doesn't know yet. Both accessors below go through this so
 *  the glyph and the label can never disagree about an unknown type. */
const resolveType = (dataType: string) =>
  COLUMN_TYPES.find((type) => type.value === dataType) ?? COLUMN_TYPES[0];

/** Glyph for a column's `dataType`. Falls back to the generic text glyph — the
 *  backend could add a type before the frontend knows its icon, and a header
 *  with no glyph would misalign against its neighbours. */
export const columnTypeIcon = (dataType: string): string => resolveType(dataType).icon;

/** Translated label for a column's `dataType`. Falls back the SAME way the glyph
 *  does, rather than interpolating the raw value into a key: an unknown type
 *  would otherwise render the missing key itself (`datasource_column_type_foo`)
 *  straight into a field label. */
export const columnTypeLabel = (dataType: string, t: (key: string) => string): string =>
  t(`datasource_column_type_${resolveType(dataType).value}`);

/** A well-formed example value for a `dataType` — shown as the Add-row field's
 *  placeholder and named in its error, so the expected shape is visible before
 *  the mistake rather than only after it. Same `resolveType` fallback as the
 *  label, for the same reason: an unknown type must not render a raw key.
 *
 *  The values themselves are DATA, not copy: they are identical in both locales
 *  and each one is verified to pass `isCellValid` for its own type. */
export const columnTypeExample = (dataType: string, t: (key: string) => string): string =>
  t(`datasource_column_example_${resolveType(dataType).value}`);
